import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, getRouteApi, useChildMatches } from "@tanstack/react-router";
import {
  AppShell,
  Burger,
  Group,
  Text,
  NavLink,
  Breadcrumbs,
  Divider,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { LogoutButton } from "@components/logout-button";

import { IconChartDots2, IconTools } from "@tabler/icons-react";

import { TanstackMantineNavLink } from "@components/tanstack-maintine-link";
const authenticatedRoute = getRouteApi("/_authenticated");

const withIconProps = (Comp: typeof IconTools): React.ReactNode => {
  return <Comp size={16} stroke={1.5} />;
};

const AppBreadcrumbs: React.FC = () => {
  const matches = useChildMatches();

  // TODO: breadcrumbs
  const deepestMatch = useMemo(() => {
    let max = matches[0];

    matches.forEach((match) => {
      if (!max || match.index > max.index) {
        max = match;
      }
    });
    return max;
  }, [matches]);

  const pathParts = useMemo(() => {
    if (!deepestMatch) {
      return [];
    }

    return deepestMatch.pathname.split("/").filter((val) => {
      // pathname e.g. '/app/players/1234/insights'
      return val !== "" && val !== "app";
    });
  }, [deepestMatch]);

  if (!pathParts.length) {
    return null;
  }

  return (
    <>
      <Breadcrumbs>
        {pathParts.map((pp, idx) => {
          return <Text key={idx}>{pp}</Text>;
        })}
      </Breadcrumbs>
      <Divider mt="sm" mb="sm" />
    </>
  );
};

export const AppLayout = React.memo((props) => {
  const [isNavOpen, { toggle: toggleIsNavOpen }] = useDisclosure();
  const { t } = useTranslation();

  const { userInfo } = authenticatedRoute.useLoaderData();

  return (
    <AppShell
      header={{ height: 60 }}
      footer={{ height: 60 }}
      navbar={{
        width: 220,
        breakpoint: "sm",
        collapsed: { mobile: !isNavOpen },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger
              opened={isNavOpen}
              onClick={toggleIsNavOpen}
              hiddenFrom="sm"
              size="sm"
            />
            <Text>{t("app.name")}</Text>
          </Group>
          <LogoutButton />
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="md">
        <TanstackMantineNavLink
          to="/app/accounts/$accountId/players"
          params={{ accountId: userInfo.account.id.toString() }}
          label={t("app.navBar.players")}
          leftSection={withIconProps(IconChartDots2)}
        />
        <NavLink
          href="https://www.mightier.com/clinician-toolkit/"
          label={t("app.navBar.toolkit")}
          target="__blank"
          leftSection={withIconProps(IconTools)}
        />
      </AppShell.Navbar>
      <AppShell.Main>
        <AppBreadcrumbs />
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
});

AppLayout.displayName = "InsightsLayout";
